/**
 * TaskListAddButton — "+" button for creating a new list. When at cap,
 * renders a greyed icon that shows a tap-triggered tooltip with a
 * subscription placeholder message.
 */

import React, { useCallback, useEffect, useRef } from 'react';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import Popover, { PopoverPlacement } from 'react-native-popover-view';

import { Colors } from '@/constants/theme';
import { Spacing, BorderRadius } from '@/constants/spacing';
import { getBodyFont } from '@/constants/typography';
import { useColorScheme } from '@/hooks/use-color-scheme';

const TOOLTIP_AUTO_DISMISS_MS = 2400;
const TOOLTIP_WIDTH = 260;

type TaskListAddButtonProps = {
  disabled: boolean;
  onPress: () => void;
};

export function TaskListAddButton({ disabled, onPress }: TaskListAddButtonProps) {
  const colorScheme = useColorScheme();
  const palette = Colors[colorScheme ?? 'light'];

  const [tooltipVisible, setTooltipVisible] = React.useState(false);
   
  const buttonRef = useRef<any>(null);

  useEffect(() => {
    if (!tooltipVisible) return;
    const timer = setTimeout(() => setTooltipVisible(false), TOOLTIP_AUTO_DISMISS_MS);
    return () => clearTimeout(timer);
  }, [tooltipVisible]);

  const handlePress = useCallback(() => {
    if (disabled) {
      setTooltipVisible(true);
      return;
    }
    onPress();
  }, [disabled, onPress]);

  return (
    <>
      <View ref={buttonRef} collapsable={false}>
        <TouchableOpacity
          onPress={handlePress}
          activeOpacity={0.7}
          style={[styles.button, disabled && styles.buttonDisabled]}
          accessibilityLabel={disabled ? 'Add list disabled' : 'Add list'}
        >
          <Text
            style={[
              styles.label,
              {
                color: palette.tint,
                fontFamily: getBodyFont(),
              },
            ]}
          >
            Add list
          </Text>
        </TouchableOpacity>
      </View>

      <Popover
        isVisible={tooltipVisible}
        from={buttonRef}
        onRequestClose={() => setTooltipVisible(false)}
        placement={PopoverPlacement.BOTTOM}
        arrowSize={{ width: 12, height: 8 }}
        backgroundStyle={styles.popoverBackdrop}
        popoverStyle={[styles.popover, { backgroundColor: palette.cardBackground }]}
        animationConfig={{ duration: 140 }}
      >
        <View style={styles.tooltipBody}>
          <Text style={[styles.tooltipText, { color: palette.text }]}>
            You&apos;ve reached the limit of 5 lists. To add more, see the
            account subscription page.
          </Text>
        </View>
      </Popover>
    </>
  );
}

const styles = StyleSheet.create({
  button: { paddingHorizontal: Spacing.sm, paddingVertical: Spacing.xs },
  buttonDisabled: { opacity: 0.4 },
  label: { fontSize: 14, fontWeight: '600' },
  popover: {
    borderRadius: BorderRadius.md,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.18,
    shadowRadius: 12,
    elevation: 8,
  },
  popoverBackdrop: {
    backgroundColor: 'transparent',
  },
  tooltipBody: {
    width: TOOLTIP_WIDTH,
    paddingHorizontal: Spacing.md,
    paddingVertical: Spacing.sm,
  },
  tooltipText: { fontSize: 13, lineHeight: 18 },
});
